import { CommonModule, CurrencyPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { status } from '../../../utils/enums/status.constants';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { API_URL } from '../../../utils';

export interface OverviewData {
  label: string;
  data: any;
  icon?: string;
  tooltip?: string;
}
@Component({
  selector: 'app-overview-card',
  standalone: true,
  imports: [NgClass, CommonModule, ButtonModule, PopoverModule, TranslatePipe],
  templateUrl: './overview-card.component.html',
  styleUrl: './overview-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewCardComponent {
  api = inject(API_URL);
  private readonly translateService = inject(TranslateService);

  private readonly currencyLabels = new Set([
    'CASE_DETAIL.TOTAL_CLAIM_AMOUNT',
    'CASE_DETAIL.PUBLIC_INSURANCE_AMOUNT',
    'CASE_DETAIL.OUT_OF_POCKET',
    'CASE_DETAIL.SAVINGS_AMOUNT',
    'CASE_DETAIL.POLICY_PREMIUM',
    'CASE_DETAIL.INSURANCE_EXEMPTION',
    'CASE_DETAIL.TOTAL_PREMIUMS',
    'CASE_DETAIL.CAPITAL_LOSS',
  ]);

  private readonly currencyFormatter = new Intl.NumberFormat('el-GR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  @Input({ required: true }) claimStatus: any;
  @Input({ required: true }) data: OverviewData[] = [];
  // @Input({ required: true }) dateStarted: any;
  @Input() dateStarted?: any;
  @Input() showStatusIcon = true;
  @Input() showHospitalization = true;
  @Input({ required: true }) sectionToScroll: string = '';
  @Input({ required: true }) sectionName: string = '';
  @Input() investigationResult!: string | null;
  @Input() isInvestigated!: boolean | null;
  @Input() enableSplitLayout = false;
  @Input() surgeriesDescription?: string | null;
  @Input() tpaDischargeIcd10Code?: string | null;
  @Input() tpaDischargeIcd10Description?: string | null;
  @Input() clinic?: string | null;
  @Input() hospital?: string | null;
  @Input() admissionType?: string | null;

  formatMetricValue(metric: OverviewData): string {
    const { label, data } = metric;

    if (label === 'CASE_DETAIL.PRE_CLAIM_ANTICIPATION') {
      return this.toDisplayValue(data);
    }

    if (label === 'CASE_DETAIL.INCIDENT_TYPE') {
      const rawValue = String(data).trim();
      if (!rawValue || rawValue === 'null' || rawValue === 'undefined') {
        return '-';
      }
      const key = rawValue.startsWith('CASE_DETAIL.')
        ? rawValue
        : `CASE_DETAIL.${rawValue}`;
      return this.translateService.instant(key);
    }

    if (data == null && data !== 0) {
      return '-';
    }

    if (typeof data === 'number' && this.isCurrencyLabel(label)) {
      return `${this.currencyFormatter.format(data)} €`;
    }

    return String(data);
  }

  private toDisplayValue(value: any): string {
    return value != null || value === 0 ? String(value) : '-';
  }

  private isCurrencyLabel(label: string): boolean {
    return !!label &&
      (this.currencyLabels.has(label) ||
        (label.startsWith('CASE_DETAIL.') &&
          /AMOUNT|PREMIUM|COST|LOSS|SAVINGS|OUT_OF_POCKET/.test(label)));
  }
  get leftColumnData(): OverviewData[] {
    const splitIndex = this.data.findIndex(
      (item) => item.label === 'CASE_DETAIL.INCIDENT_TYPE'
    );

    if (splitIndex === -1) {
      return this.data;
    }

    return this.data.slice(0, splitIndex + 1);
  }

  get rightColumnData(): OverviewData[] {
    const splitIndex = this.data.findIndex(
      (item) => item.label === 'CASE_DETAIL.INCIDENT_TYPE'
    );

    if (splitIndex === -1) {
      return [];
    }

    return this.data.slice(splitIndex + 1);
  }

  get incidentTypeMetric(): OverviewData | null {
    return this.data.find(
      (item) => item.label === 'CASE_DETAIL.INCIDENT_TYPE'
    ) ?? null;
  }

  daysSinceStarted: any;
  isCoreact(): boolean {
    return this.api.includes('coreact') || this.api.includes('localhost');
  }

  isCoassess(): boolean {
    return this.api.includes('coassess');
  }

  getClaimStatusName(): string {

    if (this.isCoreact()) {

      if (!this.investigationResult) {
        if (this.isInvestigated === true) {
          return 'OVERVIEW.STATUS_UNDER_INVESTIGATION';
        } else {
          return '-';
        }
      } else {
        const investigation = this.investigationResult.trim().toUpperCase();
        switch (investigation) {

          case 'PROVEN':

            return 'OVERVIEW.STATUS_PROVEN';

          case 'POSTACCIDENTINSURANCE':
            return 'OVERVIEW.STATUS_POSTACCIDENTINSURANCE';

          case 'PREEXISTINGDAMAGE':
            return 'OVERVIEW.STATUS_PREEXISTINGDAMAGE';

          case 'INFLATEDCHARGES':
            return 'OVERVIEW.STATUS_INFLATEDCHARGES';
          case 'SUSPICIOUS':
            return 'OVERVIEW.STATUS_SUSPICIOUS';

          case 'NOTPROVEN':
            return 'OVERVIEW.STATUS_NOT_PROVEN';
          default:
            return '-';
        }

      }
    }
    if (this.isCoassess()) {

      if (!this.claimStatus) return '-';

      const status = this.claimStatus.trim().toUpperCase();

      switch (status) {

        case 'APPROVED':
          return 'OVERVIEW.STATUS_APPROVED';

        case 'REJECTED':
          return 'OVERVIEW.STATUS_REJECTED';

        case 'UNDER_INVESTIGATION':
        case 'UNDER INVESTIGATION':
          return 'OVERVIEW.STATUS_UNDER_INVESTIGATION';

        case 'PENDING':
          return 'OVERVIEW.STATUS_PENDING';

        default:
          return '-';
      }
    }
    return '-';
  }


  getCaseStatusIcon() {

    if (this.isCoreact()) {

      if (!this.investigationResult) {
        if (this.isInvestigated === true) {
          return 'text-xl fa-solid fa-magnifying-glass mr-3 ml-5 text-white bg-warning';
        }
        else {
          return 'text-xl fa-solid fa-clock mr-3 ml-5 text-white bg-gray-300';
        }
      }

      const investigation = this.investigationResult.trim().toUpperCase();

      switch (investigation) {

        case 'FRAUD':
        case 'INFLATEDCHARGES':
        case 'PROVEN':
        case 'PREEXISTINGDAMAGE':
        case 'POSTACCIDENTINSURANCE':
          return 'text-xl fa fa-exclamation-triangle mr-2 ml-2 text-white bg-danger';

        case 'SUSPICIOUS':
          return 'text-xl fa-solid fa-magnifying-glass mr-3 ml-5 text-white bg-warning';

        case 'NO FRAUD':
        case 'NOTPROVEN':
          return 'text-xl fa-sharp fa-solid fa-square-check mr-3 ml-5 text-white bg-success';

        default:
          return 'text-xl fa-solid fa-circle mr-3 ml-5 text-white bg-secondary';
      }
    }

    if (this.isCoassess()) {

      if (!this.claimStatus) {
        return 'text-xl fa-solid fa-clock mr-3 ml-5 text-white bg-gray-300';
      }

      const status = this.claimStatus.trim().toUpperCase();

      switch (status) {
        case 'UNDER INVESTIGATION':
          return 'text-xl fa-solid fa-magnifying-glass mr-3 ml-5 text-white bg-warning';

        case 'PENDING':
          return 'text-xl fa-solid fa-clock mr-3 ml-5 text-white bg-gray-300';

        case 'APPROVED':
          return 'text-xl fa-sharp fa-solid fa-square-check mr-3 ml-5 text-white bg-success';

        case 'REJECTED':
          return 'text-xl fa fa-exclamation-triangle mr-2 ml-2 text-white bg-danger';

        default:
          return 'text-xl fa-solid fa-clock mr-3 ml-5 text-white bg-gray-300';
      }
    }
    return 'text-xl fa-solid fa-clock mr-3 ml-5 text-white bg-gray-300';
  }
  
  getSeverityBadge(severity: string): {
    badgeClass: string;
    iconClass?: string;
  } {
    let badgeClass: string;
    let iconClass: string | undefined;
    if (!severity) {
      badgeClass = 'bg-secondary';
      iconClass = undefined;
      return { badgeClass, iconClass };
    }
    if (typeof severity !== 'string') {
      console.error(`severity expects string`);
      badgeClass = 'bg-secondary';
      iconClass = undefined;
      return { badgeClass, iconClass };
    }

    switch (severity.toLowerCase()) {
      case 'low':
      case 'χαμηλή':
        badgeClass = 'bg-success';
        iconClass = 'fa-solid fa-check';
        break;

      case 'medium':
      case 'μέτρια':
        badgeClass = 'bg-warning';
        iconClass = 'fa-solid fa-exclamation';
        break;

      case 'high':
      case 'υψηλή':
        badgeClass = 'bg-danger';
        iconClass = 'fa-solid fa-exclamation-triangle';
        break;

      case 'legitimate':
      case 'νόμιμη':
        badgeClass = 'bg-success';
        iconClass = 'fa-solid fa-check';
        break;

      default:
        badgeClass = 'bg-secondary';
        iconClass = undefined;
    }

    return { badgeClass, iconClass };
  }
  
  scrollToSection(): void {
    const element = document.getElementById(this.sectionToScroll);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 60,
        behavior: 'smooth',
      });
    }
  }
}
